import {
  collection,
  db,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  increment,
} from '@/lib/firebase/firebase';
import { ModelId } from '@poppy/models';
import type { ChatActionFirebaseType } from '@poppy/firebase';
import type { ProductEntitlement } from '@poppy/utils';
import type { TrialBookingAssignment } from '@poppy/utils';

// Write this as a field's value to remove the field from the user doc, rather
// than leaving a `null` tombstone behind — so "absent" stays the only way a
// field can be unset, and the optional types say what the doc actually holds.
// Valid in `addOrUpdate` (setDoc with merge) and in `repos` updates; the cast is
// because Firestore's sentinel isn't part of any field's declared type.
export const REMOVE_FIELD = deleteField() as unknown as undefined;

export interface UserSubscriptionDataType {
  subscriptionLastUpdated: number | null;
  isSubscribed: boolean;
  isPaid: boolean;
  isRefunded: boolean;
  isFreeTrial: boolean;
  stage?: string;
  name?: string;
  phone?: string;
  payments?: string;
  stripeCustomerId?: string;
  emails: string[];
  adminGaveFreeAccess?: boolean;
  subscriptionPlan?: SubscriptionPlanType;
  // White-label partner whose customer this is (see lib/whiteLabel/partnerCustomers).
  partnerId?: string;
  appsumoTier?: number;
  // IDs of linked rows in the Supabase `subscriptions` table (optional — older
  // customers predate this field).
  userSubscriptionIds?: string[];
  // Row ids an admin unlinked; automatic linking skips them (see the schema).
  unlinkedSubscriptionIds?: string[];
  // v2: entitlement resolved from the subscription's `subscription_products`
  // row(s). Read-time only — never persisted to the user doc. Undefined when
  // the subscription has no matching product (backward-compat: old formation).
  productEntitlement?: ProductEntitlement;
}

export type UserTallyFormType = {
  onboarding_completed?: boolean;
  responses?: any;
};

type UserCalendlyCallType = {
  booked_at: string;
};

// What the getpoppy.ai/group-onboarding embed posts back after a Cal.com seat is
// booked. Older users carry the Calendly event/invitee URIs here instead, with no
// start time, so every field stays optional.
export type UserCallBookingDetailsType = {
  uid?: string;
  start?: string; // ISO 8601 UTC
  end?: string; // ISO 8601 UTC
  rep?: string;
  join?: string;
  provider?: string;
  [key: string]: unknown;
};

export type UserCalendlyType = {
  call_booked?: boolean;
  calls?: UserCalendlyCallType[];
  booking_details?: UserCallBookingDetailsType;
  booked_at?: string; // when they clicked book, not the call time
  // The call step was waived instead of booked (e.g. no open slots).
  skipped?: boolean;
  skip_reason?: 'no_availability' | string;
  skipped_at?: string;
  skip_details?: any;
};

export type UserRoleType = 'API_ACCESS' | string;

export type SubscriptionPlanType = 'BASIC' | 'STARTER' | 'CREATOR' | 'POWER' | 'TEAM' | 'APPSUMO';

type UsedAiToolLimitsType = {
  landingPageCreate?: {
    used?: number;
    creatorCouponId?: string;
    powerCouponId?: string;
    expiresAt?: number;
  };
};

export type UserType = {
  id: string;
  authUserId: string;
  name?: string;
  signUpDate: number;
  chatCount: number;
  currentMonthUsageUSD: number;
  customReferralLink?: string;
  // Explicit admin block; overrides derived billing access.
  adminBlock?: { reason: string; by: string; at: number } | null;
  totalCost: number;
  oct2StartNewCalcCost: number;
  onboardingCompleted?: boolean;
  trialBookingAssignment?: TrialBookingAssignment;
  email: string;
  photoURL: string;
  referralURL?: string;
  affiliateId?: string;
  roles?: UserRoleType[];
  tally_form?: UserTallyFormType;
  calendly?: UserCalendlyType;
  // Terminal onboarding step (invite-team). `completed` covers both invites
  // sent and the skip button — the resolver only needs to know not to show
  // the step again. Only relevant to users whose tally answers say they have
  // a team (see hasTeamFromTally).
  teamInvite?: { completed?: boolean; skipped?: boolean; completed_at?: string };
  // ISO timestamp of the one-time teams-offer bonus grant (5,000 first-month
  // team credits). Presence = claimed; the claim route refuses a second grant.
  teamsOfferBonusGrantedAt?: string;
  // ISO timestamp of the one-time Instagram library tour bonus; presence = claimed.
  libraryTourBonusGrantedAt?: string;
  subscriptionPlan?: SubscriptionPlanType;
  usedAiToolLimits?: UsedAiToolLimitsType;
  hasSeenDashboardVideo?: boolean;
  lastUsedModel?: ModelId;
  // Brand voice a new chat pre-selects. null / unset → new chats start with "No brand".
  defaultBrandId?: string | null;
  // The brand built by the onboarding run the user is currently in — written the
  // moment the create wizard hands off, and the only thing onboarding resumes
  // from. Absent means "no brand yet in this run", which is what makes the wizard
  // run again after a reset even for someone with a shelf full of brands.
  onboardingBrandId?: string;
  pinnedChatActions?: ChatActionFirebaseType[];
  recentChatActions?: ChatActionFirebaseType[];
} & UserSubscriptionDataType;

export type UserDocumentType = Omit<UserType, 'id'>;

async function getByAuthUserId(authUserId: string) {
  const usersRef = collection(db, 'users');
  const usersQuery = query(usersRef, where('authUserId', '==', authUserId));
  const usersQuerySnapshot = await getDocs(usersQuery);

  if (usersQuerySnapshot.empty) return null;

  const userDoc = usersQuerySnapshot.docs[0];

  const userDocumentData = userDoc.data() as UserDocumentType;

  const user = {
    id: userDoc.id,
    ...userDocumentData,
  };

  return user;
}

async function getById(userId: string): Promise<UserType | null> {
  const userDoc = doc(db, 'users', userId);
  const userSnapshot = await getDoc(userDoc);

  if (userSnapshot.exists()) {
    const userDocumentData = userSnapshot.data() as UserDocumentType;

    return { id: userSnapshot.id, ...userDocumentData };
  } else {
    console.log('No User found for ID:', userId);
    return null;
  }
}

async function getByEmail(email: string) {
  const usersRef = collection(db, 'users');
  const usersQuery = query(usersRef, where('email', '==', email));
  const usersQuerySnapshot = await getDocs(usersQuery);

  if (!usersQuerySnapshot.empty) {
    return usersQuerySnapshot.docs[0];
  }

  return null;
}

function subscribe(userId: string, callback: (user: UserType) => void) {
  const userDocRef = doc(db, 'users', userId);
  const unsubscribe = onSnapshot(userDocRef, async docSnapshot => {
    if (docSnapshot.exists()) {
      const userData = docSnapshot.data() as UserDocumentType;

      callback({
        id: userId,
        ...userData,
      });
    }
  });
  return unsubscribe;
}

async function addOrUpdate(userId: string, user: UserDocumentType): Promise<UserType> {
  const userRef = doc(db, 'users', userId);
  await setDoc(userRef, user, { merge: true });

  return { id: userId, ...user };
}

const users = {
  addOrUpdate,
  getByAuthUserId,
  getById,
  getByEmail,
  subscribe,
};

export default users;
